'use client'

import { IEnvService } from '@/core/services/env/env-service'

import { type Messaging } from 'firebase/messaging'
import { NotificationModel } from '../types/models/notification.model'
import { createFirebaseConfig } from './config'

let messaging: Messaging | null = null

const initMessaging = async (
  envService: IEnvService,
): Promise<Messaging | null> => {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers are not supported in this browser')
    return null
  }
  if (messaging) return messaging

  const [{ initializeApp }, { getMessaging }] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ])

  const app = initializeApp(createFirebaseConfig(envService))
  messaging = getMessaging(app)
  return messaging
}

export function createRequestNotificationPermission({
  envService,
}: {
  envService: IEnvService
}) {
  return async function (): Promise<{ fcmToken: string } | null> {
    try {
      const messaging = await initMessaging(envService)
      if (!messaging) return null

      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        console.warn('Notification permission not granted')
        return null
      }

      const { getToken } = await import('firebase/messaging')

      const fcmToken = await getToken(messaging)
      if (!fcmToken) return null

      return { fcmToken }
    } catch (err) {
      console.error('Failed to get FCM token:', err)
      return null
    }
  }
}

export function createMessagesListener({
  envService,
}: {
  envService: IEnvService
}) {
  return async function (
    onNotification: (notification: NotificationModel) => void,
  ) {
    const messaging = await initMessaging(envService)
    if (!messaging) {
      return { unsubscribe: () => {} }
    }

    const { onMessage } = await import('firebase/messaging')

    const unsubscribe = onMessage(messaging, ({ notification, data }) => {
      if (data?.metaData) {
        const { metaData, ...rest } = data
        return onNotification({
          notification,
          metaData: JSON.parse(metaData),
          data: rest,
        })
      }
      onNotification({ notification, data })
    })

    return { unsubscribe }
  }
}
